import { useMutation } from '@tanstack/react-query'
import { signIn, signOut } from 'next-auth/react'
import { toast } from 'sonner'

import { customCredentialsErrorCodes } from '../auth-providers/auth-provider-errors'
import type { CredentialsSignInSchema } from '../auth-services/auth-credentials-schemas'

/**
 * Sign in user with email/password.
 *
 * In case of successful sign in, the user will be automatically redirected to the `callbackUrl`. This means handling `{ ok: true }` result is unnecessary.
 *
 * In case of an error, returns the error details that should be presented to the user.
 */
export const signInWithCredentialsInClientComponent = async ({
  credentials,
  callbackUrl,
}: {
  credentials: CredentialsSignInSchema
  callbackUrl: string
}): Promise<
  | { ok: true }
  | {
      ok: false
      errorCode:
        | 'serverError'
        | 'incorrectCredentials'
        | 'userInactive'
        | 'userPending'
        | 'passwordLoginNotAllowed'
    }
> => {
  try {
    // Initiate a signin flow. Handles CSRF protection.
    const result = await signIn('credentials', {
      email: credentials.email,
      password: credentials.password,
      redirect: false, // Do not redirect in case of an error. Handle the error in the code below.
      redirectTo: callbackUrl, // Due to the `redirect: false`, we need to explicitly set the redirect URL for a successful flow.
    })

    if (result === undefined || result.error === undefined || result.error === null) {
      return { ok: true }
    }

    if (result.error === 'CredentialsSignin') {
      switch (result.code) {
        case customCredentialsErrorCodes.credentials_incorrect_credentials_error: {
          return { ok: false, errorCode: 'incorrectCredentials' }
        }
        case customCredentialsErrorCodes.credentials_user_inactive_error: {
          return { ok: false, errorCode: 'userInactive' }
        }
        case customCredentialsErrorCodes.credentials_user_pending_error: {
          return { ok: false, errorCode: 'userPending' }
        }
        case customCredentialsErrorCodes.credentials_user_role_error: {
          return { ok: false, errorCode: 'passwordLoginNotAllowed' }
        }
        case customCredentialsErrorCodes.credentials_validation_error:
        case customCredentialsErrorCodes.credentials_unknown_error:
        default: {
          return { ok: false, errorCode: 'serverError' }
        }
      }
    }

    return { ok: false, errorCode: 'serverError' }
  } catch {
    // Throws if fetch requests inside `signIn()` throw.
    return { ok: false, errorCode: 'serverError' }
  }
}

/**
 * Sign in user with Google.
 *
 * In case of successful sign in, the user will be automatically redirected to the `callbackUrl`. This means handling `{ ok: true }` result is unnecessary.
 *
 * In case of an error, the user will be redirected to the auth error page. So handling `{ ok: false }` is not required too.
 */
export const signInWithGoogleInClientComponent = async ({
  callbackUrl,
}: {
  callbackUrl: string
}): Promise<{ ok: true } | { ok: false; errorCode: 'serverError' }> => {
  try {
    await signIn('google', {
      redirect: true,
      redirectTo: callbackUrl,
    })

    return { ok: true }
  } catch {
    // Throws if fetch requests inside `signIn()` throw.
    return { ok: false, errorCode: 'serverError' }
  }
}

export const linkGoogleAccountInClientComponent = async (): Promise<
  { ok: true } | { ok: false; errorCode: 'serverError' }
> => {
  try {
    await signIn('google')

    return { ok: true }
  } catch {
    // Throws if fetch requests inside `signIn()` throw.
    return { ok: false, errorCode: 'serverError' }
  }
}

export const signOutInClientComponent = async (): Promise<{
  ok: boolean
}> => {
  try {
    await signOut({
      redirect: true,
      redirectTo: '/',
    })
    return { ok: true }
  } catch {
    // Throws if fetch requests inside `signOut()` throw.
    return { ok: false }
  }
}

export const useSignOutInClientComponent = () => {
  const $signOutInClientComponent = useMutation({
    mutationFn: signOutInClientComponent,
    onSuccess: (result) => {
      if (!result) {
        toast.error('Server error. Please try again later.')
      }
    },
  })

  return $signOutInClientComponent
}
